/**
 * @ngdoc component
 * @name flowSignalrClient
 * @module flowingly.services
 * @description  A component for subscribing to (and publishing) signalR events.
 * ### Notes
 * This component shows the data entered by the user for the table, when they completed the step.
 * ### Properties
 * #### Bindings
 * * signalrServerUrl: url to signalR end point (communicationsApi)
 * 
 * @usage
 * ```
     <flow-signalr-client signalr-server-url="https://communications.flowingly.net"></flow-signalr-client>
 * ```
*/
import {
  FlowModelEditingEvent,
  IFlowDeletedMessage,
  IFlowModelDeletedMessage,
  IFlowModelEditingMessage,
  IFlowModelPublishedMessage,
  IFlowModelPublishRequestedMessage,
  IFlowModelUnpublishedChangesMessage,
  IFlowModelUnpublishedMessage,
  IFutureStateCreatedMessage,
  IFutureStateDeletedMessage,
  IFutureStateUpdatedMessage,
  IInvokeHubMethodMessage,
  IProcedureCreatedMessage,
  IProcedureDeletedMessage,
  IProcedurePublishedMessage,
  IProcedurePublishRequestedMessage,
  IProcedureUpdatedMessage,
  ITagGroupDeletedMessage,
  ITagGroupUpdatedMessage,
  IUserCreatedMessage,
  IUserDeletedMessage,
  IUserUpdatedMessage
} from '@Shared.Angular/@types/pubSub';
import { Services } from '@Shared.Angular/@types/services';
import { toCamelCaseDeep } from './case-converter';
import angular, { ITimeoutService } from 'angular';

function parseSignalrMessage<T>(rawMessage: string): T {
  return toCamelCaseDeep<T>(JSON.parse(rawMessage));
}

angular.module('flowingly.services').component('flowSignalrClient', {
  bindings: {
    signalrServerUrl: '<'
  },
  controller: [
    'pubsubService',
    '$timeout',
    'sessionService',
    'auditService',
    'APP_CONFIG',
    'tokenService',
    function (
      pubsubService: Services.PubSubService,
      $timeout: ITimeoutService,
      sessionService: Services.SessionService,
      auditService: Services.AuditService,
      APP_CONFIG: Services.APP_CONFIG,
      tokenService: Services.TokenService
    ) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const $ctrl = this;

      let connection: SignalR.Hub.Connection = undefined;
      let hub: SignalR.Hub.Proxy;
      const serverUrl = $ctrl.signalrServerUrl || APP_CONFIG.signalrServerUrl;
      const subscriberId = 'runner.signalR.client';

      function startSignalr() {
        if (sessionService.getSignalR() === false) return;
        // connection already establised or in initialization phase
        if (connection) {
          return;
        }

        //start the signal r hub
        connection = $.hubConnection(serverUrl);
        connection.logging = false;
        // the name of the hub should match the HubName defined in
        // Flowingly.Core.Communications
        hub = connection.createHubProxy('NotificationsHub');

        //Define the SignalR communications before connecting, otherwise
        //"No hubs have been subscribed to.The client will not receive data from hubs"
        //error is thrown

        //each event to be handled, needs to be subscribed to here
        hub.on('actordeleted', (message) => {
          log('actorDeleted');
          const normalizedMessage = normalizeAndAuditNotification(message); // I decided to add these here as I wanted to be able to pick and choose which event to add it to
          pubsubService.publish('SIGNALR_ACTOR_DELETED', normalizedMessage);
        });

        hub.on('userCreated', (rawMessage: string) => {
          log('userCreated', rawMessage);
          const message = parseSignalrMessage<IUserCreatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('SIGNALR_USER_CREATED', message);
        });

        hub.on('userUpdated', (rawMessage: string) => {
          log('userUpdated', rawMessage);
          const message = parseSignalrMessage<IUserUpdatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('SIGNALR_USER_UPDATED', message);
        });

        hub.on('userDeleted', (rawMessage: string) => {
          log('userDeleted', rawMessage);
          const message = parseSignalrMessage<IUserDeletedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('SIGNALR_USER_DELETED', message);
        });

        hub.on('workflowpublished', (rawMessage: string) => {
          log('workflowpublished', rawMessage);
          const message =
            parseSignalrMessage<IFlowModelPublishedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('SIGNALR_WORKFLOW_PUBLISHED', message);
        });

        hub.on('flowModelPublishRequested', (rawMessage: string) => {
          log('flowModelPublishRequested', rawMessage);
          const message =
            parseSignalrMessage<IFlowModelPublishRequestedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish(
            'SIGNALR_FLOW_MODEL_PUBLISH_REQUESTED',
            message
          );
        });

        hub.on('procedurePublishRequested', (rawMessage: string) => {
          log('procedurePublishRequested', rawMessage);
          const message =
            parseSignalrMessage<IProcedurePublishRequestedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('SIGNALR_PROCEDURE_PUBLISH_REQUESTED', message);
        });

        hub.on('workflowunpublished', (rawMessage: string) => {
          log('workflowunpublished', rawMessage);
          const message =
            parseSignalrMessage<IFlowModelUnpublishedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('SIGNALR_WORKFLOW_UNPUBLISHED', message);
        });

        hub.on('newflowmodelsaved', (message) => {
          log('newflowmodelsaved', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_NEW_FLOW_MODEL_SAVED',
            normalizedMessage
          );
        });

        hub.on('flowModelNameChanged', (message) => {
          log('flowModelNameChanged', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_WORKFLOW_NAME_CHANGED',
            normalizedMessage
          );
        });

        hub.on('workflowHasUnPublishedChages', (rawMessage: string) => {
          log('workflowHasUnPublishedChages', rawMessage);
          const message =
            parseSignalrMessage<IFlowModelUnpublishedChangesMessage>(
              rawMessage
            );
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish(
            'SIGNALR_WORKFLOW_UNPUBLISHED_CHANGES',
            message
          );
        });

        // SignalR Events specific to the runner
        hub.on('runnercompletestep', (message) => {
          log('runnercompletestep', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_COMPLETE_STEP',
            normalizedMessage
          );
        });
        hub.on('runnerreassignstep', (message) => {
          log('runnerreassignstep', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_REASSIGN_STEP',
            normalizedMessage
          );
        });
        hub.on('runnerstartflow', (message) => {
          log('runnerstartflow', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish('SIGNALR_RUNNER_START_FLOW', normalizedMessage);
        });

        hub.on('runnerwithdrawflow', (message) => {
          log('runnerwithdrawflow', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_WITHDRAW_FLOW',
            normalizedMessage
          );
        });
        hub.on('userprofileupdated', (message) => {
          log('userprofileupdated', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_USER_PROFILE_UPDATED',
            normalizedMessage
          );
        });
        hub.on('categoryDeleted', (message) => {
          log('categoryDeleted', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_SETUP_CATEGORY_DELETED',
            normalizedMessage
          );
        });

        hub.on('flowModelDeleted', (rawMessage: string) => {
          log('flowModelDeleted', rawMessage);
          const message =
            parseSignalrMessage<IFlowModelDeletedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('SIGNALR_SETUP_FLOW_MODEL_DELETED', message);
        });

        hub.on('futureStateCreated', (rawMessage: string) => {
          log('futureStateCreated', rawMessage);
          const message =
            parseSignalrMessage<IFutureStateCreatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('FUTURE_STATE_CREATED', message);
        });

        hub.on('futureStateUpdated', (rawMessage: string) => {
          log('futureStateUpdated', rawMessage);
          const message =
            parseSignalrMessage<IFutureStateUpdatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('FUTURE_STATE_UPDATED', message);
        });

        hub.on('futureStateDeleted', (rawMessage: string) => {
          log('futureStateDeleted', rawMessage);
          const message =
            parseSignalrMessage<IFutureStateDeletedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('FUTURE_STATE_DELETED', message);
        });

        hub.on('runnerUserNotificationCountChanged', (message) => {
          log('runnerUserNotificationCountChanged', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_USER_NOTIFICATION_COUNT_CHANGED',
            normalizedMessage
          );
        });
        hub.on('runnerNewFlowCommentCount', (message) => {
          log('runnerNewFlowCommentCount', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_NEW_FLOW_COMMENT_COUNT',
            normalizedMessage
          );
        });
        hub.on('runnerNewFlowComment', (message) => {
          log('runnerNewFlowComment', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_NEW_FLOW_COMMENT',
            normalizedMessage
          );
        });
        hub.on('runnerUserTeamUpdated', (message) => {
          log('runnerUserTeamUpdated', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_USER_TEAM_UPDATED',
            normalizedMessage
          );
        });
        hub.on('runnerStepWebhookProcessed', (message) => {
          log('runnerStepWebhookProcessed', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_STEP_WEBHOOK_UPDATED',
            normalizedMessage
          );
        });

        hub.on('runnerNewStepTaskComment', (message) => {
          log('runnerNewStepTaskComment', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_NEW_STEP_TASK_COMMENT',
            normalizedMessage
          );
        });

        hub.on('runnerStepTaskCreated', (message) => {
          log('runnerStepTaskCreated', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_STEP_TASK_CREATED',
            normalizedMessage
          );
        });

        hub.on('runnerStepTaskUpdated', (message) => {
          log('runnerStepTaskUpdated', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_STEP_TASK_UPDATED',
            normalizedMessage
          );
        });

        hub.on('runnerStepTaskCancelled', (message) => {
          log('runnerStepTaskCancelled', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_STEP_TASK_CANCELLED',
            normalizedMessage
          );
        });

        hub.on('runnerStepIntegrationStateChanged', (message) => {
          log('runnerStepIntegrationStateChanged', message);
          const normalizedMessage = normalizeAndAuditNotification(message);
          pubsubService.publish(
            'SIGNALR_RUNNER_STEP_INTEGRATION_PROCESSING_UPDATED',
            normalizedMessage
          );
        });

        hub.on('tagGroupCreated', (rawMessage: string) => {
          log('tagGroupCreated', rawMessage);
          const message =
            parseSignalrMessage<ITagGroupUpdatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('TAG_GROUP_CREATED', message.tagGroup);
        });

        hub.on('tagGroupUpdated', (rawMessage: string) => {
          log('tagGroupUpdated', rawMessage);
          const message =
            parseSignalrMessage<ITagGroupUpdatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('TAG_GROUP_UPDATED', message.tagGroup);
        });

        hub.on('tagGroupDeleted', (rawMessage: string) => {
          log('tagGroupDeleted', rawMessage);
          const message =
            parseSignalrMessage<ITagGroupDeletedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('TAG_GROUP_DELETED', message.tagGroupId);
        });

        hub.on('procedureCreated', (rawMessage: string) => {
          log('procedureCreated', rawMessage);
          const message =
            parseSignalrMessage<IProcedureCreatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('PROCEDURE_CREATED', message);
        });

        hub.on('procedureUpdated', (rawMessage: string) => {
          log('procedureUpdated', rawMessage);
          const message =
            parseSignalrMessage<IProcedureUpdatedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('PROCEDURE_UPDATED', message);
        });

        hub.on('procedureDeleted', (rawMessage: string) => {
          log('procedureDeleted', rawMessage);
          const message =
            parseSignalrMessage<IProcedureDeletedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('PROCEDURE_DELETED', message);
        });

        hub.on('procedurePublished', (rawMessage: string) => {
          log('procedurePublished', rawMessage);
          const message =
            parseSignalrMessage<IProcedurePublishedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('PROCEDURE_PUBLISHED', message);
        });

        hub.on('flowDeleted', (rawMessage: string) => {
          log('flowDeleted', rawMessage);
          const message = parseSignalrMessage<IFlowDeletedMessage>(rawMessage);
          auditService.handledNotification(message.systemAuditId);
          pubsubService.publish('WORKFLOW_DELETED', message.flowId);
        });

        hub.on('flowModelEditingStarted', (rawMessage: string) => {
          log('flowModelEditingStarted');
          normalizeAndAuditNotification(rawMessage);
          const message =
            parseSignalrMessage<IFlowModelEditingMessage>(rawMessage);
          pubsubService.publish('SIGNALR_FLOW_MODEL_EDITING_STARTED', message);
        });

        hub.on('flowModelEditingActive', (rawMessage: string) => {
          log('flowModelEditingActive');
          normalizeAndAuditNotification(rawMessage);
          const message =
            parseSignalrMessage<IFlowModelEditingMessage>(rawMessage);
          pubsubService.publish('SIGNALR_FLOW_MODEL_EDITING_ACTIVE', message);
        });

        pubsubService.subscribe(
          'INVOKE_HUB_METHOD',
          (event: FlowModelEditingEvent, data: IInvokeHubMethodMessage) => {
            if (
              hub &&
              connection &&
              connection.state === $.signalR.connectionState.connected
            ) {
              hub
                .invoke(data.method, ...data.args)
                .done((result) => {
                  if (data.onSuccess) data.onSuccess(result);
                })
                .fail((error) => {
                  log('Hub invoke failed: ' + data.method, error);
                  if (data.onError) data.onError(error);
                });
            } else {
              log('Hub invoke skipped - not connected: ' + data.method);
              if (data.onError)
                data.onError(new Error('SignalR not connected'));
            }
          },
          subscriberId
        );

        connection.connectionSlow(() => {
          log('SignalR connection is slow. Connection Id=' + connection.id);
        });

        //handle the errors thrown from hub
        connection.error((error) => {
          log('SignalR error: ' + error);
        });

        // log that we are reconnecting
        connection.reconnecting(() => {
          log(
            'SignalR connection is reconnecting. connection Id=' + connection.id
          );
        });

        // handle disconnection: retry in 5 seconds.
        connection.disconnected(() => {
          if (connection.lastError) {
            log(
              'SignalR connection disconnected. Reason: ' +
                connection.lastError.message
            );
          } else {
            log(
              'SignalR hub connection disconnected. Connection Id=' +
                connection.id
            );
          }
          $timeout(() => {
            startConnection(true);
          }, 5000);
        });

        //initiate connection
        log('Initiating SignalR connection, url = ' + serverUrl);
        startConnection();
      }

      function normalizeAndAuditNotification(message: string): string {
        const data = parseSignalrMessage(message);
        auditService.receivedNotification(data);
        return JSON.stringify(data);
      }

      function isSessionUserExist() {
        const user = sessionService.getUser();
        return user !== null && user !== undefined;
      }

      function startConnection(isReconnect?) {
        const reestablishStr = isReconnect ? 're-' : '';

        connection
          .start({ waitForPageLoad: false })
          .done(() => {
            log(
              'SignalR connection ' +
                reestablishStr +
                'established! connection Id=' +
                connection.id
            );

            // Before publising READY_FOR_SIGNALR you MUST ensure the user is stored in the session.
            if (!isSessionUserExist()) {
              log(
                'ERROR: ',
                'Could not map SignalR connection to User because User was not in the SessionService.'
              );
            } else {
              const userId = sessionService.getUser().id;
              const businessId = tokenService.getTenant().id;

              //register this client connection with the logged in user
              //so that it gets mapped on the hub on the server side.
              hub
                .invoke('mapUserConnection', { userId, businessId })
                .done(() => {
                  log('Mapping this connection to user Id= ' + userId);
                  pubsubService.publish('SIGNALR_CONNECTED');
                })
                .fail((error) => {
                  log('SignalR registration failed. Error:  ' + error);
                  pubsubService.publish('SIGNALR_CONNECT_FAILED');
                });
            }
          })
          .fail((e) => {
            log(
              'SignalR connection could not be ' +
                reestablishStr +
                'established.',
              e
            );
            pubsubService.publish('SIGNALR_CONNECT_FAILED');
          });
      }

      function log(event, message?: string) {
        if (APP_CONFIG.enableConsoleLogging) {
          console.log(event, message || '');
        }
      }

      // start signalR on when READY_FOR_SIGNALR published
      $ctrl.$onInit = () => {
        pubsubService.subscribe(
          'READY_FOR_SIGNALR',
          startSignalr,
          subscriberId
        );
      };

      //Destroy
      $ctrl.$onDestroy = () => {
        log('SignalR client closing...');
        connection.stop();
        pubsubService.unsubscribeAll(subscriberId);
      };
    }
  ]
});
